import { dxtToLocalServerTime, useShiftNavigator } from "@dexteel/mesf-core";
import { useNavigate } from "react-router-dom";

import {
  AddBox as AddBoxIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Memory as MemoryIcon,
  PlaylistAdd as PlaylistAddIcon,
  Straighten,
} from "@mui/icons-material";
import { useCallback, useState } from "react";
import { MenuOptionType } from "../../../../controls/contextMenu/models/MenuOptionType";
import { useCasterProcessesContext } from "../context/CasterProcessesContext";
import { ProcessByShift } from "../models/ProcessByShift";

export const useCasterOptionFunctions = () => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActiveModal, setShowActiveModal] = useState(false);

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);

  const [messageDeleteModal, setMessageDeleteModal] = useState("");
  const [messageActiveModal, setMessageActiveModal] = useState("");
  const [messageUnlinkModal, setMessageUnlinkModal] = useState("");

  const {
    state: {},
    actions: { setSelectedProcess },
  } = useCasterProcessesContext();

  const { shiftInfo } = useShiftNavigator();
  const navigate = useNavigate();

  //Create
  const createData = useCallback(() => {
    setSelectedProcess({
      ProcessId: 0,
      casterOpen: shiftInfo?.CurrentProductionDate ?? new Date(),
      casterClose: shiftInfo?.CurrentProductionDate ?? new Date(),
      casterOpenString: "",
      casterCloseString: "",
      HeatId: 0,
      HeatName: "",
      IsActive: true,
    });
    setShowEditModal(true);
  }, [shiftInfo?.CurrentProductionDate, setSelectedProcess]);

  //Edit
  const editData = (data: ProcessByShift) => {
    setSelectedProcess(data);
    setShowEditModal(true);
  };

  //Link
  const linkData = (data: ProcessByShift) => {
    setSelectedProcess(data);
    setShowLinkModal(true);
  };

  //Unlink
  const unlinkData = (data: ProcessByShift) => {
    setSelectedProcess(data);
    const dateOpen = dxtToLocalServerTime(data.Start, "HH:mm");
    setMessageUnlinkModal(
      `The heat ${data.HeatName} at ${dateOpen} will be unlinked.  Do you want to continue?`,
    );
    setShowUnlinkModal(true);
  };

  //Active Data
  const activeData = (data: ProcessByShift) => {
    setSelectedProcess(data);
    const dateOpen = dxtToLocalServerTime(data.Start, "HH:mm");
    setMessageActiveModal(
      data.HeatName
        ? `The process at ${dateOpen} with the heat ${data.HeatName} will be activated. Do you want to continue?`
        : `The process at ${dateOpen} will be activated. Do you want to continue?`,
    );
    setShowActiveModal(true);
  };

  const inactiveData = (data: ProcessByShift) => {
    setSelectedProcess(data);
    const dateOpen = dxtToLocalServerTime(data.Start, "HH:mm");

    setMessageDeleteModal(
      data.HeatName
        ? `The process at ${dateOpen} with the heat ${data.HeatName} will be deleted. Do you want to continue?`
        : `The process at ${dateOpen} will be deleted. Do you want to continue?`,
    );
    setShowDeleteModal(true);
  };

  //Toggle Status
  const toggleStatusData = (data: ProcessByShift) => {
    if (data.IsActive) inactiveData(data);
    else activeData(data);
  };

  //Get Menu options
  const getMenuOptions = (data: ProcessByShift) => {
    let options: MenuOptionType[] = [];
    if (data) {
      if (data.IsActive) {
        if (data.ProcessId) {
          options.push({
            name: "See in Caster DE",
            key: "go_to_caster",
            onClick: () => {
              goToDataEntry(data.ProcessId);
            },
            icon: <MemoryIcon />,
            style: { fontWeight: "bold" },
          });
          options.push({
            name: "See in Torches DE",
            key: "go_to_torches_data_entry",
            onClick: () => goToTorchesDataEntry(data.ProcessId),
            icon: <Straighten />,
          });
        }

        if (data.HeatId) {
          options = options.concat([
            // {
            // 	name: "See in Heat Table",
            // 	key: "go_to_heat",
            // 	onClick: () => goToHeats(data.HeatId as string),
            // 	style: { fontWeight: "bold" },
            // },
            {
              name: "Unlink Process",
              key: "unlink",
              onClick: () => unlinkData(data),
              icon: <LinkOffIcon />,
            },
          ]);
        }

        if (!data.HeatId) {
          options = options.concat([
            {
              name: "Link Process",
              key: "link",
              onClick: () => linkData(data),
              icon: <LinkIcon />,
            },
          ]);
        }

        options.push({
          name: "Edit Process",
          key: "edit_process",
          onClick: () => editData(data),
          icon: <EditIcon />,
        });
      }

      options.push({
        name: data.IsActive ? "Delete Process" : "Undelete Process",
        key: "toggle_status",
        onClick: () => toggleStatusData(data),
        icon: data.IsActive ? <DeleteIcon /> : <AddBoxIcon />,
      });
    } else {
      options.push({
        name: "New Process",
        key: "create_new_process",
        onClick: () => createData(),
        icon: <PlaylistAddIcon />,
      });
    }
    return options;
  };

  //navigate functions
  const goToHeats = (HeatId: string | null) => {
    if (HeatId === null) return;
    navigate(`/meltshop-heats/${HeatId}`);
  };

  const goToDataEntry = (ProcessId: number | null) => {
    if (ProcessId === null) return;
    navigate(`/caster-data-entry/${ProcessId}`);
  };

  const goToTorchesDataEntry = (ProcessId: number | null) => {
    if (ProcessId === null) return;
    navigate(`/caster-torches/${ProcessId}`);
  };

  return {
    createData,
    editData,
    linkData,
    unlinkData,
    toggleStatusData,
    getMenuOptions,
    goToHeats,
    goToDataEntry,
    showActiveModal,
    showDeleteModal,
    showEditModal,
    showLinkModal,
    showUnlinkModal,
    showCommentModal,
    messageActiveModal,
    messageDeleteModal,
    messageUnlinkModal,
    setShowActiveModal,
    setShowDeleteModal,
    setShowEditModal,
    setShowLinkModal,
    setShowUnlinkModal,
    setShowCommentModal,
    setMessageActiveModal,
    setMessageDeleteModal,
    setMessageUnlinkModal,
  };
};
